// Builds a simulated 12-team league: teams, rosters via a talent draft, a
// round-robin schedule, finished periods, and the current period's lineups.

import {
  buildDefaultSettings,
  teamAbbreviation,
  UpdateTeamRequest,
  type LeagueSettings,
  type LeagueSettingsVersion,
  type WizardAnswers,
} from '@fsp/domain';
import type { ChatMessage, LeagueHat } from '@fsp/domain';
import type { AiManager, DraftPick, DraftState } from '@fsp/draft';
import type { RosterPreset, StatLine } from '@fsp/sports-core';
import { football, nflTeams } from '@fsp/sports-football';
import { scorePlayer } from '@fsp/scoring';
import { seedChat } from './chat';
import { buildProSchedule, playerEvents, type GameEvent, type ProGame } from './games';
import { PLAYER_SEEDS, type SimPosition } from './players';
import { hashSeed, rng } from './rng';
import { emptyWaiverState, initWaivers, type WaiverState } from './waivers';
import { emptyTradeState, initTrades, type TradeState } from './trades';
import { playoffMatchupsFor } from './playoffs';
import { emptyAwardState, initAwards, type AwardState } from './awards';
import { buildHistory, buildPeriodDetails, type PeriodTeamDetail } from './history';
import { pastProGames, pastWeek } from './past-weeks';
import type { PastSeasonFact } from '@fsp/awards';

/** A simulated draft room: the pure engine's state. Its chat is the league channel. */
export interface SimDraft {
  state: DraftState;
}

export interface SimPlayer {
  id: string;
  name: string;
  shortName: string;
  positions: [SimPosition];
  proTeam: string;
  ppg: number;
  injury: { status: string; note: string } | null;
}

export interface SimTeam {
  id: string;
  name: string;
  abbreviation: string;
  ownerName: string;
  colors: { primary: string; secondary: string };
  roster: string[];
  /** Current-period lineup: slot rows in roster-preset order. */
  lineup: { slot: string; slotIndex: number; playerId: string | null }[];
  faabRemaining: number;
  isMine: boolean;
  /** Set when an AI manager holds the seat (DESIGN.md §2.16). */
  ai: AiManager | null;
}

export interface SimMatchup {
  id: string;
  period: number;
  home: string;
  away: string;
}

export interface SimState {
  /**
   * The zone this browser's reader is told times in (B-181): their account's,
   * or this device's for a visitor. Set by the client and read by labels and
   * sentences only; every rule runs in `settings.identity.timeZone`. Absent,
   * the league's own zone, named.
   */
  readerZone?: string;
  seed: number;
  leagueId: string;
  leagueName: string;
  sport: string;
  /** 'setup' leagues have members but no rosters, schedule, or scores yet. */
  status: 'setup' | 'in-season';
  /** ms; set while status is 'setup'. */
  draftAt: number | null;
  isCommissioner: boolean;
  commissionerName: string;
  /** Ken has a seat. False for a league he has only been invited to. */
  joined: boolean;
  /** Eight characters, no ambiguous letters; what the invite link carries. */
  inviteCode: string;
  /** The league's rules, versioned by the API later; the sim keeps the current one. */
  settings: LeagueSettings;
  season: number;
  currentPeriod: number;
  regularSeasonEnd: number;
  playoffTeams: number;
  /** ms, when the current period's window starts (Thursday). */
  periodStart: number;
  periodEnd: number;
  sundayUtcMidnight: number;
  players: Map<string, SimPlayer>;
  teams: SimTeam[];
  schedule: SimMatchup[];
  /** Final team scores for completed periods: the starters' own points that week (B-527). */
  finals: Map<string, number>; // `${period}:${teamId}`
  /**
   * Each team's lineup in each week before the one the league was made at,
   * `${period}:${teamId}`: the lineup it was made with, and last week's as it
   * stood at that week's kickoff (`keepWeek`), kept apart so a week gone by
   * keeps its box score after a swap made now (`past-weeks.ts`).
   */
  pastLineups: Map<string, SimTeam['lineup']>;
  /** The pro games of each week before the one the league was made at, by period (`pastProGames`). */
  pastGames: Map<number, ProGame[]>;
  proGames: ProGame[];
  byes: string[];
  /** Events per player for the current period's games. */
  events: Map<string, GameEvent[]>;
  /** Waivers, claims, the transaction log, priority (sim/waivers.ts). */
  waiverState: WaiverState;
  /** Trades: the book, planned answers, narration (sim/trades.ts). */
  tradeState: TradeState;
  /** The current settings' scoring, kept in sync by saveSettings. */
  scoring: LeagueSettings['scoring'];
  /** Every save is a version (RULES_SCHEMA.md §17); index 0 is the wizard's. */
  settingsVersions: LeagueSettingsVersion[];
  /** The draft room, once opened (sim/draft.ts). */
  draft: SimDraft | null;
  /** The league channel (CHAT.md): talk, AI talk, system posts. */
  chat: ChatMessage[];
  /** Things advanceChat has already posted (e.g. 'final:3'). */
  chatFlags: Set<string>;
  rosterPreset: RosterPreset;
  /** Bench, optimal lineup, top starter, and projection behind each finished period's total. */
  periodDetails: Map<string, PeriodTeamDetail>; // `${period}:${teamId}`
  /** Past seasons, as the rollover will summarize them; empty for a new league. */
  history: PastSeasonFact[];
  /** Minted awards, unlocked milestones, and what has been posted (sim/awards.ts). */
  awards: AwardState;
  /**
   * The weekly hat (PLAN-2026-10-30-the-weekly-hat): the latest settled
   * week's, decided with its awards (sim/awards.ts), or null.
   */
  hat: LeagueHat | null;
}

export interface TeamSeed {
  name: string;
  abbreviation: string;
  owner: string;
  pro: string;
}

export interface LeagueOptions {
  season?: number;
  currentPeriod?: number;
  id?: string;
  name?: string;
  /** Members in draft order; index 0 is always Ken. */
  teams?: readonly TeamSeed[];
  status?: 'setup' | 'in-season';
  /** ms; only read when status is 'setup'. null means not scheduled yet. */
  draftAt?: number | null;
  isCommissioner?: boolean;
  commissionerName?: string;
  /** Whether Ken has a seat. A league he can join by code starts false. */
  joined?: boolean;
  /** Full settings; built from the wizard answers when omitted. */
  settings?: LeagueSettings;
  /**
   * Seed for the real-world parts (pro schedule, byes, every player's game
   * events) so leagues in the same world see the same Sunday. Defaults to
   * `seed`.
   */
  worldSeed?: number;
  /** Past seasons to remember; defaults to none. */
  pastSeasons?: number;
  /** Owner names who won those seasons, oldest first; null lets the seed decide. */
  pastChampions?: readonly (string | null)[];
}

/** Ken's Sunday-afternoon friends league: 12 teams, he runs it. */
export const FRIENDS_SEEDS: readonly TeamSeed[] = [
  { name: "Ken's Vikings", abbreviation: 'KEN', owner: 'Ken', pro: 'MIN' },
  { name: "Dave's Packers", abbreviation: 'DAV', owner: 'Dave', pro: 'GB' },
  { name: "Sarah's Shockers", abbreviation: 'SAR', owner: 'Sarah', pro: 'DET' },
  { name: "Priya's Pack", abbreviation: 'PRI', owner: 'Priya', pro: 'BUF' },
  { name: 'Marcus Mayhem', abbreviation: 'MAR', owner: 'Marcus', pro: 'KC' },
  { name: "Jen's Jets", abbreviation: 'JEN', owner: 'Jen', pro: 'NYJ' },
  { name: 'Tom Terrific', abbreviation: 'TOM', owner: 'Tom', pro: 'NE' },
  { name: "Ana's Anacondas", abbreviation: 'ANA', owner: 'Ana', pro: 'PHI' },
  { name: 'Luis Lightning', abbreviation: 'LUI', owner: 'Luis', pro: 'LAC' },
  { name: "Mia's Monsoon", abbreviation: 'MIA', owner: 'Mia', pro: 'SEA' },
  { name: 'Chris Cross', abbreviation: 'CHR', owner: 'Chris', pro: 'BAL' },
  { name: "Omar's Army", abbreviation: 'OMA', owner: 'Omar', pro: 'DEN' },
];

/** The coworker league: 10 teams, Maya runs it, Ken is just a manager. */
export const OFFICE_SEEDS: readonly TeamSeed[] = [
  { name: 'Purple Reign', abbreviation: 'PRP', owner: 'Ken', pro: 'MIN' },
  { name: 'Maya Hem', abbreviation: 'MAY', owner: 'Maya', pro: 'SF' },
  { name: 'Standup Sackers', abbreviation: 'STU', owner: 'Raj', pro: 'DAL' },
  { name: 'Ctrl Alt Defeat', abbreviation: 'CAD', owner: 'Elena', pro: 'CHI' },
  { name: 'Deadline Dodgers', abbreviation: 'DDL', owner: 'Sam', pro: 'MIA' },
  { name: 'Merge Conflicts', abbreviation: 'MRG', owner: 'Wei', pro: 'PIT' },
  { name: 'Reply All Stars', abbreviation: 'RPL', owner: 'Nadia', pro: 'CIN' },
  { name: 'Coffee Achievers', abbreviation: 'COF', owner: 'Jordan', pro: 'TB' },
  { name: 'Out of Office', abbreviation: 'OOO', owner: 'Grace', pro: 'ATL' },
  { name: 'Punt to Monday', abbreviation: 'PNT', owner: 'Diego', pro: 'HOU' },
];

/** The family league: 8 teams, still filling in, drafts this week. */
export const FAMILY_SEEDS: readonly TeamSeed[] = [
  { name: 'Dad Strength', abbreviation: 'DAD', owner: 'Ken', pro: 'MIN' },
  { name: 'The Cousins', abbreviation: 'CUZ', owner: 'Lily', pro: 'LA' },
  { name: 'Grandma Knows', abbreviation: 'GMA', owner: 'Rose', pro: 'ARI' },
  { name: 'Little Brother', abbreviation: 'BRO', owner: 'Nate', pro: 'LV' },
  { name: 'Sister Act', abbreviation: 'SIS', owner: 'Kate', pro: 'NO' },
  { name: 'Cousin Eddie', abbreviation: 'EDD', owner: 'Eddie', pro: 'CAR' },
  { name: 'Open seat', abbreviation: '---', owner: '', pro: 'TEN' },
  { name: 'Open seat', abbreviation: '---', owner: '', pro: 'IND' },
];

const DRAFT_TARGETS: Record<SimPosition, number> = { QB: 2, RB: 4, WR: 5, TE: 2, K: 1, DST: 1 };

function shortName(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return name;
  return `${parts[0]![0]}. ${parts.slice(1).join(' ')}`;
}

export function createSim(seed = 2026, opts: LeagueOptions = {}): SimState {
  const r = rng(hashSeed(seed, 'league'));
  const season = opts.season ?? 2026;
  const currentPeriod = opts.currentPeriod ?? 3;
  const seeds = opts.teams ?? FRIENDS_SEEDS;
  const status = opts.status ?? 'in-season';
  const inSeason = status === 'in-season';
  const worldSeed = opts.worldSeed ?? seed;

  // Sunday of the current period: Sept 27, 2026 for period 3.
  const firstSunday = Date.UTC(season, 8, 13);
  const sundayUtcMidnight = firstSunday + (currentPeriod - 1) * 7 * 24 * 3600 * 1000;
  const periodStart = sundayUtcMidnight - 4 * 24 * 3600 * 1000 + 7 * 3600 * 1000; // Wed 03:00Z
  const periodEnd = periodStart + 7 * 24 * 3600 * 1000;

  // Players
  const players = new Map<string, SimPlayer>();
  PLAYER_SEEDS.forEach((s, i) => {
    const id = `p${String(i + 1).padStart(3, '0')}`;
    players.set(id, {
      id,
      name: s.name,
      shortName: s.pos === 'DST' ? s.name : shortName(s.name),
      positions: [s.pos],
      proTeam: s.team,
      ppg: s.ppg,
      injury: null,
    });
  });

  // Teams
  const teams: SimTeam[] = seeds.map((t, i) => {
    const pro = nflTeams.find((n) => n.id === t.pro)!;
    return {
      id: `t${i + 1}`,
      name: t.name,
      abbreviation: t.abbreviation,
      ownerName: t.owner,
      colors: { primary: pro.palette.primary, secondary: pro.palette.secondary },
      roster: [],
      lineup: [],
      faabRemaining: 100 - r.int(0, 45),
      isMine: i === 0,
      ai: null,
    };
  });

  // Snake draft by talent with positional targets; a little noise so rosters differ run to run by seed.
  // Noise is drawn once per player, then the sort is pure: a comparator that
  // consumes randomness orders differently across JS engines (V8 vs JSC).
  // A 'setup' league has not drafted: rosters stay empty and nobody is hurt yet.
  const noisy = new Map([...players.values()].map((p) => [p.id, p.ppg + r.next() * 1.5]));
  const pool = [...players.values()].sort((a, b) => noisy.get(b.id)! - noisy.get(a.id)!);
  const taken = new Set<string>();
  const counts = teams.map(
    () => ({ QB: 0, RB: 0, WR: 0, TE: 0, K: 0, DST: 0 }) as Record<SimPosition, number>,
  );
  const rounds = inSeason ? Object.values(DRAFT_TARGETS).reduce((a, b) => a + b, 0) : 0;
  // The draft is remembered pick by pick so the recap can grade it.
  const draftPicks: DraftPick[] = [];
  for (let round = 0; round < rounds; round++) {
    const order =
      round % 2 === 0 ? teams.map((_, i) => i) : teams.map((_, i) => teams.length - 1 - i);
    for (const ti of order) {
      const need = (pos: SimPosition) => counts[ti]![pos] < DRAFT_TARGETS[pos];
      // Kickers and defenses go in the last two rounds regardless of talent.
      const lateRound = round >= rounds - 2;
      const pick =
        pool.find((p) => {
          if (taken.has(p.id)) return false;
          const pos = p.positions[0];
          if (!need(pos)) return false;
          if (!lateRound && (pos === 'K' || pos === 'DST')) return false;
          return true;
        }) ??
        // Targets exhausted at every position that has players left: take the best
        // available skill player so every roster still fills to 15.
        pool.find((p) => {
          if (taken.has(p.id)) return false;
          const pos = p.positions[0];
          return pos !== 'K' && pos !== 'DST';
        });
      if (!pick) continue;
      taken.add(pick.id);
      teams[ti]!.roster.push(pick.id);
      counts[ti]![pick.positions[0]]++;
      draftPicks.push({
        overall: draftPicks.length + 1,
        round: round + 1,
        slot: order.indexOf(ti) + 1,
        teamId: teams[ti]!.id,
        playerId: pick.id,
        at: 0,
        by: 'user',
      });
    }
  }

  // Injuries: a few Out, a few Questionable, chosen deterministically. Ken's are placed after
  // the pro schedule exists so his problem player is in a late game (see below).
  const injuryPool = r.shuffle([...taken].filter((id) => !teams[0]!.roster.includes(id)));
  injuryPool
    .slice(0, 5)
    .forEach(
      (id) => (players.get(id)!.injury = { status: 'Q', note: 'Limited in practice Friday.' }),
    );
  injuryPool
    .slice(5, 8)
    .forEach((id) => (players.get(id)!.injury = { status: 'O', note: 'Ruled out.' }));

  // Pro schedule for the current period
  const { games: proGames, byes } = buildProSchedule(
    nflTeams.map((t) => t.id),
    sundayUtcMidnight,
    hashSeed(worldSeed, currentPeriod),
  );
  const gameByTeam = new Map<string, ProGame>();
  for (const g of proGames) {
    gameByTeam.set(g.home, g);
    gameByTeam.set(g.away, g);
  }

  // Lineups: best projection per slot, honoring eligibility, for everyone. Ken's demo problem
  // is injected afterwards so it lands on a real starter.
  const rosterPreset = football.rosterPresets[0]!;
  const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
  fillLineups(players, teams, rosterPreset, gameByTeam, byes);

  // Ken's demo problem: pick a starter whose game hasn't kicked off at the default sim time
  // and who has a healthy, eligible, also-not-started bench replacement, then rule him Out.
  // The health bar then has a real problem with a real one-tap fix. Plus a Questionable
  // receiver for the news feed.
  {
    const ken = teams[0]!;
    const lateStart = sundayUtcMidnight + 20 * 3600 * 1000; // 4 PM ET window and later
    const startOf = (id: string) => gameByTeam.get(players.get(id)!.proTeam)?.startAt ?? 0;
    const isStarter = (row: SimTeam['lineup'][number]) =>
      slotDefs.get(row.slot)!.kind === 'starter';
    const benchIds = ken.lineup
      .filter((row) => !isStarter(row) && row.playerId)
      .map((row) => row.playerId!);
    let chosen: string | null = null;
    for (const row of ken.lineup) {
      if (!isStarter(row) || !row.playerId || startOf(row.playerId) < lateStart) continue;
      const def = slotDefs.get(row.slot)!;
      const replacement = benchIds.find(
        (id) =>
          def.eligible.includes(players.get(id)!.positions[0]) &&
          !players.get(id)!.injury &&
          startOf(id) >= lateStart,
      );
      if (replacement) {
        chosen = row.playerId;
        break;
      }
    }
    chosen ??= ken.lineup.find((row) => isStarter(row) && row.playerId)?.playerId ?? null;
    if (chosen) players.get(chosen)!.injury = { status: 'O', note: 'Knee sprain, week to week.' };
    const kenQ = ken.roster
      .map((id) => players.get(id)!)
      .find((p) => p.positions[0] === 'WR' && p.id !== chosen && !p.injury);
    if (kenQ) kenQ.injury = { status: 'Q', note: 'Full practice Friday, no designation expected.' };
  }

  // Events for every player playing this period. Out players produce nothing.
  const events = new Map<string, GameEvent[]>();
  for (const p of players.values()) {
    const g = gameByTeam.get(p.proTeam);
    if (!g) continue;
    if (p.injury?.status === 'O') {
      events.set(p.id, []);
      continue;
    }
    events.set(
      p.id,
      playerEvents(p.id, g.id, p.positions[0], p.ppg, hashSeed(worldSeed, currentPeriod)),
    );
  }

  // League schedule: round robin, 12 teams, 11 rounds then repeats.
  const schedule: SimMatchup[] = inSeason
    ? buildSchedule(
        teams.map((t) => t.id),
        1,
        14,
      )
    : [];

  // FINALS FOR COMPLETED PERIODS, from the players' own points (B-527): each
  // team's starters that week, in the lineup it was made with, which its
  // box score, its bench and its best lineup are read from too. They were a
  // number drawn for the team until 2026-10-26, with nobody's points behind
  // it.
  const pastLineups = new Map<string, SimTeam['lineup']>();
  for (const team of teams) {
    const made = team.lineup.map((row) => ({ ...row }));
    for (let period = 1; inSeason && period < currentPeriod; period++)
      pastLineups.set(`${period}:${team.id}`, made);
  }
  const pastGames = new Map<number, ProGame[]>();
  for (let period = 1; inSeason && period < currentPeriod; period++)
    pastGames.set(
      period,
      pastProGames(
        nflTeams.map((t) => t.id),
        sundayUtcMidnight - (currentPeriod - period) * 7 * 24 * 3600 * 1000,
        hashSeed(worldSeed, period),
      ),
    );
  const finals = new Map<string, number>();
  for (let period = 1; inSeason && period < currentPeriod; period++)
    for (const team of teams)
      finals.set(
        `${period}:${team.id}`,
        pastWeek(pastLineups.get(`${period}:${team.id}`)!, players, period).score,
      );

  const periodDetails = buildPeriodDetails(teams, players, pastLineups, finals, currentPeriod - 1);
  const history = inSeason
    ? buildHistory(seed, teams, season, opts.pastSeasons ?? 0, opts.pastChampions ?? [])
    : [];

  const draftAt = inSeason
    ? null
    : opts.draftAt === undefined
      ? sundayUtcMidnight + 25 * 3600 * 1000
      : opts.draftAt;
  const leagueName = opts.name ?? "Ken's League";
  const settings =
    opts.settings ??
    buildDefaultSettings(football, {
      name: leagueName,
      teamCount: teams.length,
      season,
      timeZone: 'America/Chicago',
      draftScheduledAt: draftAt === null ? null : new Date(draftAt).toISOString(),
      // THE DEMO BIDS. A new league claims worst-team-first by default since
      // 2026-09-24 (B-135), but the demo's leagues, the tour and this
      // package's tests were built around a budget — the Waivers screen's
      // bid field, "$62 of $100 left", a run that spends it — so they ask
      // for one. A league made through the wizard here takes its answer.
      waiverSystem: 'faab',
    });

  const state: SimState = {
    seed,
    leagueId: opts.id ?? 'sim-league',
    leagueName,
    sport: football.id,
    status,
    draftAt,
    isCommissioner: opts.isCommissioner ?? true,
    commissionerName: opts.commissionerName ?? 'Ken',
    joined: opts.joined ?? true,
    inviteCode: inviteCode(hashSeed(seed, 'invite')),
    settings,
    season,
    currentPeriod,
    regularSeasonEnd: 14,
    playoffTeams: 6,
    periodStart,
    periodEnd,
    sundayUtcMidnight,
    players,
    teams,
    schedule,
    finals,
    proGames,
    byes,
    events,
    waiverState: emptyWaiverState(),
    tradeState: emptyTradeState(),
    scoring: settings.scoring,
    draft: null,
    chat: [],
    chatFlags: new Set(),
    settingsVersions: [
      {
        leagueId: opts.id ?? 'sim-league',
        version: 1,
        settings,
        createdAt: new Date(periodStart - 30 * 24 * 3600 * 1000).toISOString(),
        createdBy: opts.commissionerName ?? 'Ken',
        reason: 'League created',
        diff: [],
        effectiveFromPeriod: 1,
        approvedBy: [],
      },
    ],
    rosterPreset,
    pastLineups,
    pastGames,
    periodDetails,
    history,
    awards: emptyAwardState(),
    hat: null,
  };
  // An in-season league drafted here, a couple of weeks before Week 1, one pick a minute.
  if (inSeason && draftPicks.length) {
    const startedAt =
      periodStart - (currentPeriod - 1) * 7 * 24 * 3600 * 1000 - 10 * 24 * 3600 * 1000;
    state.draft = {
      state: {
        id: `${state.leagueId}-draft`,
        type: 'snake',
        order: teams.map((t) => t.id),
        rounds,
        pickSeconds: settings.draft.pickSeconds,
        status: 'complete',
        picks: draftPicks.map((p) => ({ ...p, at: startedAt + p.overall * 60_000 })),
        queues: {},
        deadlineAt: null,
        pausedRemaining: null,
        startedAt,
        completedAt: startedAt + (draftPicks.length + 1) * 60_000,
        ai: {},
        whenTheClockExpires: settings.draft.whenTheClockExpires,
        autopilot: {},
        autopilotOnExpiry: true,
      },
    };
  }
  state.chat = seedChat(state);
  // Last week's moves and claims run through the real engine; this week's are
  // planned. Last week's lineups are kept at its kickoff as they land, and its
  // finals with them (`keepWeek`), so the bracket is drawn after.
  initWaivers(state);
  // Past the regular season: the bracket's games, round by round from the finals.
  if (inSeason && currentPeriod > state.regularSeasonEnd) {
    for (let p = state.regularSeasonEnd + 1; p <= currentPeriod; p++)
      state.schedule.push(...playoffMatchupsFor(state, p, periodStart));
    // The lineups and my demo problem were placed for a regular-season matchup; keep them.
  }
  initTrades(state);
  // Every finished period's awards, and last week's card in the channel.
  initAwards(state);
  return state;
}

/** Eight characters from an alphabet with no 0/O, 1/I/L, so it can be read aloud. */
export function inviteCode(seed: number): string {
  const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const r = rng(seed);
  return Array.from({ length: 8 }, () => alphabet[r.int(0, alphabet.length - 1)]).join('');
}

const OPEN: Omit<TeamSeed, 'pro'> = { name: 'Open seat', abbreviation: '---', owner: '' };
const SEAT_PROS = ['TEN', 'IND', 'JAX', 'CLE', 'NYG', 'WAS', 'LV', 'CAR', 'ARI', 'NO'];

/** A short, stable id from a name: "Sunday Regret" → "sunday-regret". */
export function slugify(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'league'
  );
}

/**
 * The league the setup wizard creates (LEAGUE_SETUP.md): the commissioner
 * in seat one with a placeholder team, every other seat open, status
 * 'setup', rules built from the answers by the domain package.
 */
export function createLeagueFromWizard(
  answers: WizardAnswers,
  opts: { id: string; ownerName: string; ownerPro?: string; seed?: number; worldSeed?: number },
): SimState {
  const seed = opts.seed ?? hashSeed(opts.id, 'wizard');
  const teams: TeamSeed[] = [
    {
      name: `${opts.ownerName}'s Team`,
      abbreviation: opts.ownerName.slice(0, 3).toUpperCase(),
      owner: opts.ownerName,
      pro: opts.ownerPro ?? 'MIN',
    },
    ...Array.from({ length: Math.max(0, answers.teamCount - 1) }, (_, i) => ({
      ...OPEN,
      pro: SEAT_PROS[i % SEAT_PROS.length]!,
    })),
  ];
  const settings = buildDefaultSettings(football, answers);
  return createSim(seed, {
    id: opts.id,
    name: answers.name,
    season: answers.season,
    teams,
    status: 'setup',
    draftAt: answers.draftScheduledAt ? Date.parse(answers.draftScheduledAt) : null,
    isCommissioner: true,
    commissionerName: opts.ownerName,
    settings,
    worldSeed: opts.worldSeed ?? seed,
  });
}

/**
 * Ken's world: three leagues in the same season and period, sharing one real
 * Sunday (same pro schedule, same game events) so the hub reads as one day.
 * The friends league is the one every earlier screen was built on. A fourth
 * league, Dave's, is one Ken has been invited to but has not joined; the join
 * flow finds it by its invite code.
 */
export function createWorld(seed = 2026, opts: { currentPeriod?: number } = {}): SimState[] {
  const currentPeriod = opts.currentPeriod ?? 3;
  const friends = createSim(seed, {
    id: 'friends',
    name: "Ken's League",
    teams: FRIENDS_SEEDS,
    worldSeed: seed,
    currentPeriod,
    // Two seasons on the shelf: Ken took 2024, Dave 2025.
    pastSeasons: 2,
    pastChampions: ['Ken', 'Dave'],
  });
  const office = createSim(seed + 1, {
    id: 'office',
    name: 'Office Grinders',
    teams: OFFICE_SEEDS,
    isCommissioner: false,
    commissionerName: 'Maya',
    worldSeed: seed,
    currentPeriod,
    pastSeasons: 1,
    pastChampions: ['Maya'],
  });
  const family = createSim(seed + 2, {
    id: 'family',
    name: 'Family Cup',
    teams: FAMILY_SEEDS,
    status: 'setup',
    // Sunday 9:00 PM ET: the "Sunday night" clock preset lands with the room open.
    draftAt: friends.sundayUtcMidnight + 25 * 3600 * 1000,
    worldSeed: seed,
    currentPeriod,
  });
  const daves = createSim(seed + 3, {
    id: 'daves-dynasty',
    name: "Dave's Dynasty",
    teams: [
      { ...OPEN, pro: 'MIN' },
      { name: 'Dave Nation', abbreviation: 'DAV', owner: 'Dave', pro: 'GB' },
      { name: 'Sarah Smash', abbreviation: 'SAR', owner: 'Sarah', pro: 'DET' },
      { name: 'Priya Power', abbreviation: 'PRI', owner: 'Priya', pro: 'BUF' },
      { name: 'Marcus Mayhem', abbreviation: 'MAR', owner: 'Marcus', pro: 'KC' },
      { ...OPEN, pro: 'TEN' },
      { ...OPEN, pro: 'IND' },
      { ...OPEN, pro: 'JAX' },
    ],
    status: 'setup',
    draftAt: friends.sundayUtcMidnight + 7 * 24 * 3600 * 1000 + 24 * 3600 * 1000, // next Sunday 8 PM ET
    isCommissioner: false,
    commissionerName: 'Dave',
    joined: false,
    worldSeed: seed,
    currentPeriod,
  });
  return [friends, office, family, daves];
}

/** Take the first open seat in a league: the join flow (LEAGUE_SETUP.md). Mutates; returns null or a reason. */
export function joinLeague(
  state: SimState,
  member: { ownerName: string; teamName: string; pro: string },
): string | null {
  if (state.joined) return 'You are already in this league.';
  if (state.status !== 'setup') return `${state.leagueName} has already drafted.`;
  const seat = state.teams.find((t) => t.ownerName === '');
  if (!seat) return `${state.leagueName} is full. Ask ${state.commissionerName} to open a seat.`;
  const pro = nflTeams.find((n) => n.id === member.pro) ?? nflTeams[0]!;
  seat.name = member.teamName;
  seat.abbreviation = teamAbbreviation(member.teamName);
  seat.ownerName = member.ownerName;
  seat.colors = { primary: pro.palette.primary, secondary: pro.palette.secondary };
  for (const t of state.teams) t.isMine = t === seat;
  state.joined = true;
  return null;
}

/**
 * Rename or recolor my own team (SCENARIOS L2), on the simulated world.
 *
 * THE SERVER'S RULES, from the same schema: `UpdateTeamRequest` parses the
 * change, the three letters follow the name by `teamAbbreviation`, and a team
 * that is not mine is refused with the store's sentence. The demo is where
 * somebody tries the sheet before they have a league, and a sheet that
 * accepted here what a real league refuses would teach the wrong thing.
 *
 * Returns the reason when refused, null when done.
 */
export function editTeam(
  state: SimState,
  teamId: string,
  change: { name?: string; colors?: { primary: string; secondary: string } },
): string | null {
  const parsed = UpdateTeamRequest.safeParse(change);
  if (!parsed.success) return parsed.error.issues[0]?.message ?? 'That change is not valid.';
  const team = state.teams.find((t) => t.id === teamId);
  if (!team) return 'No team here.';
  if (!team.isMine) return 'That is not your team.';
  const { name, colors } = parsed.data;
  if (name !== undefined) {
    team.name = name;
    team.abbreviation = teamAbbreviation(name);
  }
  if (colors !== undefined) team.colors = { primary: colors.primary, secondary: colors.secondary };
  return null;
}

/** Best projection per starter slot, honoring eligibility; the rest to the bench and reserve. */
export function fillLineups(
  players: ReadonlyMap<string, SimPlayer>,
  teams: SimTeam[],
  rosterPreset: RosterPreset,
  gameByTeam: ReadonlyMap<string, ProGame>,
  byes: readonly string[],
): void {
  const slotDefs = new Map(football.slots.map((s) => [s.id, s]));
  for (const team of teams) {
    const available = new Set(team.roster);
    const proj = (id: string) => projectionFor(players.get(id)!, gameByTeam, byes);
    const rows: SimTeam['lineup'] = [];
    for (const { slot, count } of rosterPreset.slots) {
      const def = slotDefs.get(slot)!;
      for (let i = 0; i < count; i++) {
        if (def.kind !== 'starter') {
          rows.push({ slot, slotIndex: i, playerId: null });
          continue;
        }
        const candidates = [...available]
          .filter((id) => def.eligible.includes(players.get(id)!.positions[0]))
          .filter((id) => players.get(id)!.injury?.status !== 'O')
          .sort((a, b) => proj(b) - proj(a));
        const chosen = candidates[0] ?? null;
        if (chosen) available.delete(chosen);
        rows.push({ slot, slotIndex: i, playerId: chosen });
      }
    }
    // Remaining players fill bench rows in order.
    const benchRows = rows.filter((x) => slotDefs.get(x.slot)!.kind === 'bench');
    const rest = [...available].sort((a, b) => proj(b) - proj(a));
    benchRows.forEach((row, i) => (row.playerId = rest[i] ?? null));
    if (rosterPreset.reserve) {
      const irRows = rows.filter((x) => x.slot === rosterPreset.reserve!.slot);
      irRows.forEach((row, i) => (row.playerId = rest[benchRows.length + i] ?? null));
    }
    team.lineup = rows;
  }
}

/**
 * Round-robin schedule for periods `from` through `to`; odd team counts get a bye each period.
 * TODO(Q6): one opponent a week, whatever the practice league's rules say.
 */
export function buildSchedule(teamIds: readonly string[], from: number, to: number): SimMatchup[] {
  const schedule: SimMatchup[] = [];
  const ids = teamIds.length % 2 === 0 ? [...teamIds] : [...teamIds, '__bye__'];
  const n = ids.length;
  const rot = [...ids];
  for (let period = 1; period <= to; period++) {
    const pairs: [string, string][] = [];
    for (let i = 0; i < n / 2; i++) {
      const a = rot[i]!;
      const b = rot[n - 1 - i]!;
      if (a !== '__bye__' && b !== '__bye__') pairs.push(period % 2 === 0 ? [a, b] : [b, a]);
    }
    if (period >= from)
      pairs.forEach(([home, away], i) =>
        schedule.push({ id: `m${period}-${i + 1}`, period, home, away }),
      );
    // rotate all but the first
    const fixed = rot[0]!;
    const rest = rot.slice(1);
    rest.unshift(rest.pop()!);
    rot.splice(0, n, fixed, ...rest);
  }
  return schedule;
}

export function gameFor(state: SimState, player: SimPlayer): ProGame | null {
  return state.proGames.find((g) => g.home === player.proTeam || g.away === player.proTeam) ?? null;
}

/** Period projection: ppg adjusted for bye, injury, and a little matchup noise. */
export function projectionFor(
  player: SimPlayer,
  gameByTeam: ReadonlyMap<string, ProGame>,
  byes: readonly string[],
): number {
  if (byes.includes(player.proTeam) || !gameByTeam.get(player.proTeam)) return 0;
  if (player.injury?.status === 'O') return 0;
  const r = rng(hashSeed(player.id, 'proj'));
  const factor = player.injury?.status === 'Q' ? 0.85 : 1;
  return Math.round(player.ppg * factor * r.around(1, 0.08, 0.8, 1.2) * 10) / 10;
}

export function pointsFor(state: SimState, playerId: string, line: StatLine): number {
  const player = state.players.get(playerId)!;
  return scorePlayer([{ gameId: 'g', line }], player.positions, state.scoring).total;
}
